import { FormEvent, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { urls } from '@zapier/identity-utils';
import { fetch } from '../../../utils';

export type ForgotPasswordStatus = 'initial' | 'submitting' | 'success';

type ForgotPasswordErrorCode =
  | 'account-disabled'
  | 'no-user'
  | 'too-frequent'
  | 'unknown';

type ForgotPasswordResponse = {
  errors?: Array<{ type?: string }>;
};

/**
 * Result of a forgot-password submission:
 * - '' on success
 * - 'email-verification-required' when the account exists but its email isn't
 *   verified — the caller should route to the verify-email (resend) flow
 * - any {@link ForgotPasswordErrorCode} for an inline error to surface
 */
type ForgotPasswordResult =
  | ForgotPasswordErrorCode
  | 'email-verification-required'
  | '';

async function submitForgotPassword(
  email: string
): Promise<ForgotPasswordResult> {
  const resp = await fetch<ForgotPasswordResponse>(
    urls.FORGOT_PASSWORD_API_URL,
    {
      method: 'POST',
      body: { email },
    }
  );

  // Account exists but the email isn't verified yet — signal the caller to
  // route to the verify-email (resend) flow rather than show a dead-end error.
  if (
    resp.body?.errors?.some(
      error => error?.type === 'email_verification_required'
    )
  ) {
    return 'email-verification-required';
  }

  if (resp.status === 200) return '';
  if (resp.status === 403) return 'account-disabled';
  if (resp.status === 404) return 'no-user';
  if (resp.status == 429) return 'too-frequent';
  return 'unknown';
}

export function useForgotPassword() {
  const router = useRouter();
  const t = useTranslations('forgot-password');
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string | undefined>();
  const [status, setStatus] = useState<ForgotPasswordStatus>('initial');

  function resultToMessage(result: ForgotPasswordErrorCode | ''): string {
    if (result === '') return '';
    switch (result) {
      case 'account-disabled':
        return t('errors.account-disabled');
      case 'no-user':
        return t('errors.no-user');
      case 'too-frequent':
        return t('errors.too-frequent');
      case 'unknown':
        return t('errors.unknown');
    }
  }

  async function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    if (e) {
      e.preventDefault();
    }

    setStatus('submitting');
    setError(undefined);

    const result = await submitForgotPassword(email);

    if (result === 'email-verification-required') {
      // The verify-email page requires the user's email as a query param.
      // Leave status as `submitting`; the page navigates away.
      void router.push(addTruthyParamsToUrl(urls.VERIFY_EMAIL_URL, { email }));
      return;
    }

    const error = resultToMessage(result);

    setError(error);
    setStatus(error ? 'initial' : 'success');
  }

  function handleChangeEmail(value: string) {
    setEmail(value);
  }

  return {
    canSubmit: email.length > 0 && status !== 'submitting',
    email,
    error,
    handleChangeEmail,
    handleSubmit,
    status,
  };
}
